import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const REVIEWS = [["reviewed_role", "I have reviewed the role this person will hold"],
                 ["reviewed_permissions", "I have reviewed the permissions that role carries"],
                 ["reviewed_classroom", "I have reviewed their classroom assignment"],
                 ["reviewed_eligibility", "I have reviewed their recorded screening eligibility"]];

export default function HireDialog({ id, meta, applicant, onClose, onHired }) {
  const [form, setForm] = useState({ role: "teacher", room: "", reason: "", test: false,
    reviewed_role: false, reviewed_permissions: false, reviewed_classroom: false, reviewed_eligibility: false });
  const [busy, setBusy] = useState(false);

  const hire = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hiring/applications/${id}/hire`, form);
      toast.success(`Staff invitation issued to ${applicant.email} — ${data.invitation?.send?.result || "queued"}`);
      onHired();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const ready = REVIEWS.every(([k]) => form[k]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="hire-dialog">
        <DialogHeader><DialogTitle>Hire {applicant.name} and start onboarding</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            This sends the usual staff invitation. It does not create a login, grant permissions, assign a
            classroom, start payroll, or copy any screening report into their staff profile.
          </p>
          <div>
            <Label className="font-bold">Staff role to invite them as</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-2" data-testid="hire-role"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.staff_roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-bold">Intended classroom (recorded, applied later in Staff)</Label>
            <Input className="mt-2" value={form.room} data-testid="hire-room"
                   onChange={(e) => setForm({ ...form, room: e.target.value })} />
          </div>
          <div className="space-y-2">
            {REVIEWS.map(([key, label]) => (
              <label key={key} className="flex gap-3 items-start text-sm font-semibold">
                <Checkbox checked={form[key]} data-testid={`hire-${key}`}
                          onCheckedChange={(v) => setForm({ ...form, [key]: Boolean(v) })} />
                {label}
              </label>
            ))}
          </div>
          <div>
            <Label className="font-bold">Reason for the record</Label>
            <Input className="mt-2" value={form.reason} data-testid="hire-reason"
                   onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
          <label className="flex gap-3 items-center text-sm font-semibold">
            <Checkbox checked={form.test} data-testid="hire-test"
                      onCheckedChange={(v) => setForm({ ...form, test: Boolean(v) })} />
            Mark this invitation as a test
          </label>
          <div className="flex gap-3">
            <Button onClick={hire} disabled={busy || !ready} className="rounded-full font-bold bg-[#10B981] hover:bg-emerald-600"
                    data-testid="confirm-hire-btn">{busy ? "Issuing invitation…" : "Issue staff invitation"}</Button>
            <Button variant="outline" onClick={onClose} className="rounded-full font-bold">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
